import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";

export type SelectOption = {
  label: string;
  value: string;
};

type SelectProps = {
  value?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  triggerClassName?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Title shown in the modal header (native only, ignored on web) */
  title?: string;
};

export function Select({
  value,
  onValueChange,
  options,
  placeholder = "Select an option",
  disabled = false,
  isLoading = false,
  triggerClassName,
  searchable = false,
  searchPlaceholder = "Search...",
}: SelectProps) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [mutedForeground] = useCSSVariable(["--color-muted-foreground"]) as [
    string,
  ];

  const filteredOptions = React.useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const normalize = (s: string) =>
      s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const q = normalize(searchQuery.trim());
    return options.filter((opt) => normalize(opt.label).includes(q));
  }, [options, searchable, searchQuery]);

  const displayOptions = searchable ? filteredOptions : options;
  const loadingPlaceholder = (
    <span className="flex items-center">
      <svg
        className="text-brand h-4 w-4 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="text-muted-foreground ml-2">Loading...</span>
    </span>
  );

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      onOpenChange={(open) => {
        if (!open) setSearchQuery("");
      }}
    >
      <SelectPrimitive.Trigger
        disabled={disabled || isLoading}
        className={cn(
          "border-border bg-card text-foreground placeholder:text-muted-foreground flex h-12 w-full items-center justify-between rounded-lg border px-4 py-3 text-base",
          "outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "[&>span]:line-clamp-1",
          triggerClassName
        )}
      >
        <SelectPrimitive.Value
          placeholder={isLoading ? loadingPlaceholder : placeholder}
        />
        <SelectPrimitive.Icon asChild>
          <Ionicons
            name="chevron-down-outline"
            size={18}
            color={mutedForeground}
            style={{ marginLeft: 8 }}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          className={cn(
            "border-border bg-popover text-foreground relative z-50 max-h-96 min-w-32 overflow-hidden rounded-lg border shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1"
          )}
        >
          {searchable && (
            <div
              className="border-border bg-popover sticky top-0 border-b p-2"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  requestAnimationFrame(() => searchInputRef.current?.focus());
                }}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={searchPlaceholder}
                className="border-border bg-card text-foreground placeholder:text-muted-foreground w-full rounded-md border px-3 py-2 text-sm outline-none"
              />
            </div>
          )}
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)"
            )}
          >
            {displayOptions.length === 0 ? (
              <div className="text-muted-foreground px-4 py-3 text-center text-sm">
                Sem resultados
              </div>
            ) : null}
            {displayOptions.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className={cn(
                  "relative flex w-full cursor-default items-center justify-between rounded-sm px-4 py-3 text-base outline-none select-none",
                  "focus:bg-secondary focus:text-brand",
                  "data-disabled:pointer-events-none data-disabled:opacity-50",
                  "data-[state=checked]:bg-secondary data-[state=checked]:text-brand data-[state=checked]:font-semibold"
                )}
              >
                <SelectPrimitive.ItemText>
                  {option.label}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator>
                  <span className="text-brand ml-2 text-lg">✓</span>
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
